// react/src/helpers/ShopperContext.jsx
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

/**
 * Context for **shopper-facing** components — the `.s-*` mount points that run
 * on storefronts, the marketplace, and checkout.
 *
 * This is deliberately *not* AuthContext (the seller dashboard's provider, which
 * POSTs `/account/token`, GETs `/account/presence.json`, and sends
 * `X-Mayhem-Token`). Shopper pages are mostly guests, the API router lives on
 * the `api.*` vhost only, and the SSR `/shopper/*.json` plane is cookie-authed —
 * so the base transport here is just a `fetch` that sends cookies and JSON
 * against `apiBase`, plus the page's CSRF token for the rare `.json` endpoint
 * that wants one.
 *
 * On top of that base, a **logged-in shopper** also gets a session-derived API
 * access token (`PLAN-SHOPPER-REACT-SESSION-TOKEN.md`): on the first island
 * mount the provider POSTs the relative `/shopper/token` once per fresh page
 * load, persists the token in `localStorage`, and `shopperFetch` attaches it as
 * `X-Shopper-Token` on requests that target `apiBase` (the `/v1` API origin).
 * This lets `.s-*` islands call the token-authenticated `/v1` shopper API as the
 * logged-in shopper. Guests get a benign `{ token: null }`, no header, no
 * console noise, and behave exactly as before. Relative `/shopper/*.json`
 * requests never carry the header — the SSR plane's behavior is unchanged.
 *
 * On logout, every server-side shopper logout path (Phase 5 of the same plan)
 * revokes this account's outstanding `scope:'shopper-web'` tokens
 * (`server/helpers/shopperWebTokens.js`), so a token left behind in this
 * browser's `localStorage` fails on its next `/v1` call; the bounded 401-retry
 * below clears it and the client falls back to guest mode until re-login mints
 * a fresh one.
 *
 * @param {string} [apiBase] - Origin prefix for API paths, normally
 *   `window.__ENV__.API_URL` as passed by shopper.jsx. Defaults to '' only so
 *   tests and non-browser callers can opt out.
 */
const ShopperContext = createContext({
    fetch: (...args) => window.fetch(...args),
    apiFetch: (...args) => window.fetch(...args),
    apiBase: '',
    csrfToken: null,
    token: null,
});

const TOKEN_ENDPOINT = '/shopper/token';
const STORAGE_KEY = 'mayhemShopperToken';
const STORAGE_EXPIRES_KEY = 'mayhemShopperTokenExpiresAt';

// Module-level singletons, shared across every ShopperProvider on the page
// (shopper.jsx builds one provider per mounted island — see AuthContext.jsx:29
// for the same trio). This guarantees at most one POST /shopper/token per fresh
// page load regardless of island count.
let shopperTokenValue = null;      // string | null (null = confirmed guest / unresolved)
let shopperTokenPromise = null;    // in-flight POST /shopper/token
let shopperTokenResolvedAt = null; // ms; non-null once resolved for this page load

function storageGet(key) {
    try {
        if (typeof localStorage === 'undefined') return null;
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function storageClear() {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRES_KEY);
    } catch (e) {
        /* private mode / disabled storage — non-fatal */
    }
}

function storageSet(token, expiresAtMs) {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(STORAGE_KEY, token);
        localStorage.setItem(STORAGE_EXPIRES_KEY, String(expiresAtMs));
    } catch (e) {
        /* non-fatal — the in-memory singleton still carries the token */
    }
}

/**
 * POST /shopper/token unconditionally (bypasses the cache). Resolves to the
 * token string, or `null` for a guest / on any error. Never throws to callers.
 * De-dupes concurrent callers on `shopperTokenPromise`.
 */
function requestShopperToken() {
    if (shopperTokenPromise) return shopperTokenPromise;

    shopperTokenPromise = window.fetch(TOKEN_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
    })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`shopper token request failed: ${response.status}`);
            }
            const json = await response.json();
            const token = json && json.data && json.data.token;
            if (token && token.accessToken) {
                shopperTokenValue = token.accessToken;
                const expiresAtMs = token.expiresAt
                    ? new Date(token.expiresAt).getTime()
                    : Date.now() + 3600 * 1000;
                storageSet(token.accessToken, expiresAtMs);
            } else {
                // Guest, or the session ended (logout self-heal, decision #3).
                shopperTokenValue = null;
                storageClear();
            }
            shopperTokenResolvedAt = Date.now();
            return shopperTokenValue;
        })
        .catch(() => {
            // Network/HTTP error: leave whatever token we had in place, stay
            // quiet, and don't throw to the island. Mark resolved so N islands
            // don't each re-POST on a flaky page load.
            shopperTokenResolvedAt = Date.now();
            return shopperTokenValue;
        })
        .finally(() => {
            shopperTokenPromise = null;
        });

    return shopperTokenPromise;
}

/**
 * Resolve the shopper token for this page load: an in-flight promise, an already
 * resolved value, a live `localStorage` token, or a fresh POST — in that order.
 */
function ensureShopperToken() {
    if (shopperTokenPromise) return shopperTokenPromise;
    if (shopperTokenResolvedAt !== null) return Promise.resolve(shopperTokenValue);

    const stored = storageGet(STORAGE_KEY);
    const storedExpiresAt = storageGet(STORAGE_EXPIRES_KEY);
    if (stored && storedExpiresAt && Date.now() < parseInt(storedExpiresAt, 10)) {
        shopperTokenValue = stored;
        shopperTokenResolvedAt = Date.now();
        return Promise.resolve(shopperTokenValue);
    }
    if (stored) storageClear(); // expired / malformed

    return requestShopperToken();
}

/**
 * Drop the current token everywhere and allow the next `ensureShopperToken` to
 * re-resolve. Used by the bounded 401-retry.
 */
function clearShopperToken() {
    shopperTokenValue = null;
    shopperTokenResolvedAt = null;
    storageClear();
}

/**
 * True only when `url` is an absolute URL on the configured API origin. The
 * trailing-slash guard stops a look-alike host (`https://api.mayhem.my.evil.com`
 * against an `https://api.mayhem.my` base) from being treated as the API origin
 * and receiving the bearer token.
 */
function targetsApiOrigin(url, apiBase) {
    if (typeof url !== 'string' || !apiBase) return false;
    if (url === apiBase) return true;
    const base = apiBase.endsWith('/') ? apiBase : `${apiBase}/`;
    return url.startsWith(base);
}

/**
 * Reads the CSRF token the EJS layer renders into the page, so a `.s-*`
 * component can post to a session-guarded route without being handed one
 * through data-props. Prefers the `<meta name="csrf-token">` the shared header
 * partials emit on every SSR page (server/views/partials/csrf-meta.ejs), and
 * falls back to a form's hidden `csrfToken` input. Both carry the same
 * `req.session.csrfToken`.
 */
function readPageCsrfToken() {
    if (typeof document === 'undefined') return null;
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.getAttribute('content')) return meta.getAttribute('content');
    const input = document.querySelector('input[name="csrfToken"]');
    return input ? input.value : null;
}

export function ShopperProvider({ children, apiBase = '', csrfToken = null }) {
    const [token, setToken] = useState(shopperTokenValue);

    // Every provider runs this; the module singleton collapses it to one
    // POST /shopper/token per fresh page load. The first island to mount kicks
    // it off, the rest await the same promise. With no `apiBase` there is no
    // API origin to attach the token to, so there is nothing to acquire.
    useEffect(() => {
        if (!apiBase) return undefined;
        let cancelled = false;
        ensureShopperToken()
            .then((value) => {
                if (!cancelled) setToken(value);
            })
            .catch(() => {
                /* ensureShopperToken never rejects, but be defensive */
            });
        return () => {
            cancelled = true;
        };
    }, [apiBase]);

    const value = useMemo(() => {
        const performFetch = async (url, options, allowRetry) => {
            const toApi = targetsApiOrigin(url, apiBase);
            // Only the API origin ever sees the token. Relative `/shopper/*.json`
            // requests (the SSR data plane) never trigger acquisition and never
            // carry the header — their behavior is provably unchanged.
            const currentToken = toApi ? await ensureShopperToken() : null;

            const headers = { ...(options.headers || {}) };
            const hasFormData = typeof FormData !== 'undefined'
                && options.body instanceof FormData;
            if (options.body && !hasFormData && !headers['Content-Type']) {
                headers['Content-Type'] = 'application/json';
            }
            if (!headers.Accept) headers.Accept = 'application/json';

            if (currentToken && toApi) {
                headers['X-Shopper-Token'] = currentToken;
            }

            const response = await window.fetch(url, {
                ...options,
                headers,
                // Guest carts and shopper sessions both ride on cookies
                credentials: options.credentials || 'include',
            });

            // Bounded single retry: a 401 on a token-authed API call means the
            // stored token is stale (e.g. logout elsewhere). Drop it, re-mint
            // from the session once, and replay. Never loops.
            if (allowRetry && toApi && currentToken && response.status === 401) {
                clearShopperToken();
                await requestShopperToken();
                return performFetch(url, options, false);
            }

            return response;
        };

        const shopperFetch = (url, options = {}) => performFetch(url, options, true);

        return {
            fetch: shopperFetch,
            apiFetch: shopperFetch,
            apiBase,
            csrfToken: csrfToken || readPageCsrfToken(),
            token,
        };
    }, [apiBase, csrfToken, token]);

    return <ShopperContext.Provider value={value}>{children}</ShopperContext.Provider>;
}

export function useShopper() {
    return useContext(ShopperContext);
}

// For testing — reset module-level token state and stored token.
export function resetShopperTokenState() {
    shopperTokenValue = null;
    shopperTokenPromise = null;
    shopperTokenResolvedAt = null;
    storageClear();
}

export default ShopperContext;
